import { forceCheck } from 'react-lazyload';
import { autorun, makeAutoObservable, reaction, runInAction, set } from 'mobx';
import axios from 'axios';
import i18next from 'i18next';

import { createBonusProfile } from '@/lib/api/bonuses';
import { logger } from '@/lib/api/logger';
import { sendMsg } from '@/lib/api/postMessageAPI';
import { Socket } from '@/lib/api/Socket';
import { fetchBanners } from '@/lib/api/ui';
import stylesInjector from '@/helpers/additionalScripts/stylesInjector';
import { isLargeScreen, isMobile, isTablet } from '@/helpers/checkDeviceSize';
import { searchParamsToObject } from '@/helpers/convertSearchParams';
import { createEl } from '@/helpers/createEl';
import { getDisciplineCategoriesFromLineFilter } from '@/helpers/getDisciplineCategoriesFromLineFilter';
import { getDisciplinesViews } from '@/helpers/getDisciplinesViews';
import { getRandomNums } from '@/helpers/getRandomNums';
import { importGoogleFont } from '@/helpers/importGoogleFont';
import { isEqualArrays } from '@/helpers/isEqualArrays';
import { LS } from '@/helpers/localStorageHelper';
import { setLazyLoadScrollSuppressed } from '@/hooks/useLazyLoadOnScroll';
import { isShadowMode } from '@/config';
import {
  defaultLanguage,
  defaultOddsFormat,
  defaultSortingByTournaments,
  disciplineIds,
  esportDisciplines,
  getLocaleLang,
  isDemoLocation,
  lineFilterToDisciplineCategories,
  oddsFormats,
  rtlLanguages,
  viewTypesSettings,
  widgetTabs,
} from '@/config/constants';
import { type IThemeItem, type UTheme, defaultTheme, themeIsValid, themeList } from '@/theme';

import { RootStoreMobx } from '../RootStoreMobx';
import { Alert } from './Alert';
import type { DisciplineByTypeItem, DisciplinesByTypeObject } from './types';

const html = document ? document.getElementsByTagName('html')[0] : null;
const app = document ? document.getElementById('sportsbook-app') : null;

const getTheme = () => {
  const theme = LS.get<UTheme>('color_theme');
  if (theme && themeIsValid(theme)) {
    return theme;
  }
  return defaultTheme;
};

const compileTheme = (theme: UTheme): IThemeItem[] => {
  const themeSettings: IThemeItem | undefined = themeList.find(({ name }) => name === theme);

  if (!themeSettings) return [];

  const inherit: IThemeItem['inherit'] = themeSettings.inherit;
  if (inherit) {
    if (Array.isArray(inherit)) {
      const mergedSettings = inherit
        .map((themeName: UTheme) => compileTheme(themeName).concat(themeSettings))
        .flat(Infinity);

      const { themes: themesSettings } = mergedSettings.reduce(
        (acc, settings) => {
          const themeName = settings.name;
          if (!acc.names[themeName] && themeName !== theme) {
            acc.themes.push(settings);
          }
          acc.names[themeName] = true;
          return acc;
        },
        { names: {}, themes: [] },
      );

      return themesSettings.concat(themeSettings);
    }
    return compileTheme(inherit as UTheme).concat(themeSettings);
  }

  return [themeSettings];
};

export class UI {
  root: RootStoreMobx;

  constructor(root: RootStoreMobx, initialState) {
    makeAutoObservable(this);

    this.root = root;
    Object.assign(this, initialState);

    reaction(
      () => this.language,
      (language) => {
        if (language) {
          this.root.store.marketsIntervals = {};
        }
      },
    );

    /**
     * Reacts to theme loading and Shadow Mode readiness.
     * Collects all computed CSS variables from the root element
     * and stores them into `this.cssVariables`.
     *
     * Safeguards:
     * - Skips execution until theme is loaded
     * - Skips Shadow Mode until ShadowRoot host is available
     */
    autorun(() => {
      if (!this.isThemeLoaded || (isShadowMode && !this.appRoot.host)) {
        return;
      }

      const root: Element = isShadowMode
        ? (this.appRoot.host as Element)
        : document.documentElement;

      const styles = getComputedStyle(root);
      this.cssVarsiables = [...styles]
        .filter((n) => n.startsWith('--'))
        .reduce((acc, name) => {
          acc[name] = styles.getPropertyValue(name).trim();
          return acc;
        }, {});
    });
  }

  appRoot: ShadowRoot | Document = document;

  gridEventItemsPreviews = new Map();

  // [key: string] is actually [key: disciplineId]
  disciplinesViews: { [key: string]: string } = {};

  parentGaps: { [key: string]: number } = { bottom: 0, top: 0, topBottom: 0 };

  oddsFormat = defaultOddsFormat;

  theme = getTheme();

  customTheme = false;

  isSettledCoreTheme = false;

  isLoadingTheme = false;

  isThemeLoaded = false;

  isLargeScreen = isLargeScreen();

  isMobile = isMobile();

  isTablet = isTablet();

  disciplineIds: string[] = [];

  disciplinesWithTypes: DisciplineByTypeItem[] = [];

  disciplineIdsAliases = [];

  alerts = [];

  betslipAlerts = [];

  language = LS.get('locale') || defaultLanguage;

  defaultLanguage = defaultLanguage;

  betSlipOpen = false;

  isBonusesModalOpen = false;

  appWidth = window.innerWidth;

  appHeight = window.innerHeight;

  isShowQuickBets = false;

  isEventPage = false;

  lobbyFilters: {
    country: string | null;
    date: string | null;
    disciplines: string[];
    lines: string[];
    tournament: string | null;
  } = {
    country: null,
    date: null,
    disciplines: [],
    lines: [],
    tournament: null,
  };

  mainMarketsFilters = JSON.parse(LS.get('MMFilters')) || {};

  cyberLobbyView = '';

  sportLobbyView = '';

  mainLobbyView = 0;

  playVideo = false;

  blockVideo = false;

  disciplinesIsSport = 'lobby';

  activeTab = widgetTabs[0] ? widgetTabs[0].id : 'betslip';

  liveView = false;

  modalMybetsOpen = false;

  modalConditionalCashoutOpen = false;

  modalSettingsOpen = false;

  betHistoryOpen = false;

  lobbyNavMenuIsOpen = false;

  pageWithChat = false;

  mobileSliderInteraction = [];

  mainMenuExpands = [];

  lineFilter: string | null | string[] = null;

  isHideVideo = false;

  isSLG = false;

  routSlug = '';

  isTopNavOrientation = false;

  isHideCategorizer = false;

  isAnimalsRacingView = false;

  isMatchTrackerScriptLoaded = false;

  isMatchTrackerEnabled = false;

  matchTrackerSocketId: string | null = null;

  hasTaxes = false;

  isRocketAnimation = false;

  isSDK = false;

  hasParentIframe = false;

  mainFrameUrl: string = '';

  loginUrl: string = '';

  depositUrl: string = '';

  registerUrl: string = '';

  isHideExpressPresets = false;

  socketConfigIsLoaded = false;

  coreSettingsIsLoaded = false;

  isRtlOrientation = false;

  disciplineIdsSubscriptionAliases = {};

  eventsToRenderOnPageByDefault = 50;

  eventsToRenderOnPage = this.eventsToRenderOnPageByDefault;

  isShowSessionExpiredPopup = false;

  isThemeEditorMode = false;

  isDemoFreebetMode = false;

  isThemeSettledByThemeEditor = false;

  isPersonalizedEvents = false;

  presetLastBetSum = true;

  showSwipeBet = false;

  showBalance = false;

  themeEditorData = {};

  cssVarsiables = {};

  bannerBetslip: {
    name: string;
    url: string;
    height: { desktop: string; mobile: string };
  } | null = null;

  currentConditionalBetData: Record<string, unknown> | null = null;

  customizedTournaments: { tournamentId: string; disciplineId: string }[] | string[] =
    JSON.parse(LS.get('customTournaments') || '[]') || [];

  locationHistory = {
    currentLocation: null,
    list: [],
    prevLocation: null,
  };

  isLightTheme = false;

  isVip = false;

  /**
   * View type for display options.
   * Can be 'european', 'american', or 'social'.
   */
  viewType: 'european' | 'american' | 'social' = 'european';

  /**
   * If a partner specifies "viewType" through a parameter in the SDK or via a query parameter,
   * the "viewType" settings specified in "api/settings" will not be applied.
   */
  isViewTypeWasSettledByQueryParam = false;

  /**
   * Indicates whether the view is set to American.
   *
   * If `true`, the following applies:
   * - The dropdown for selecting markets to display is hidden.
   * - On the grid event card, three markets are displayed by default:
   * 'winner', 'total', and 'handicap'. Markets are displayed in a
   * vertical layout.
   */
  isAmericanView: boolean = false;

  /**
   * List of widgets allowed for display, received from coreSettings.
   */
  allowedWidgets: object = {};

  /**
   * List of client banners.
   */
  banners: object = {};

  isWasRequestToTheSettings: boolean = false;

  /**
   *  'scrollableElement' represents the main scrollable element of the application.
   *  For mobile devices, it corresponds to the element with the class 'games-layout',
   *  while for desktop, it is the element with the class auto-scroll.
   */
  scrollableElement: HTMLElement | null = null;

  /**
   * Sets top and bottom gap CSS variables on the root element.
   * @param obj - Object containing top and bottom gap values in pixels.
   */

  isCustomizedTournamentsPage: boolean = false;

  isShowLanguagePicker: boolean = true;

  setDisciplinesViews = (brand: string) => {
    this.disciplinesViews = getDisciplinesViews(defaultSortingByTournaments.includes(brand));
  };

  /**
   * Sets whether the categorizer is hidden.
   * @param isHide
   */
  setIsHideCategorizer = (isHide: boolean) => {
    this.isHideCategorizer = isHide;
  };
  setIsVip = (isVip: boolean) => {
    this.isVip = isVip;
  };

  /**
   * Sets the visibility of the language picker.
   * @param isHide
   */
  setIsHideLanguagePicker = (isHide: boolean) => {
    this.isShowLanguagePicker = !isHide;
  };

  isCustomizedHomePage: boolean = false;

  setParentGaps = (obj: { top: number; bottom: number }): void => {
    const { top, bottom } = obj;
    const topBottom = top + bottom;
    const root: HTMLElement = isShadowMode
      ? (this.appRoot.host as HTMLElement)
      : document.documentElement;

    if (root) {
      root.style.setProperty('--parent-top-gap', `${top}px`);
      root.style.setProperty('--parent-bottom-gap', `${bottom}px`);
      root.style.setProperty('--parent-top-bottom-gap', `${topBottom}px`);
    }

    this.parentGaps = { ...obj, topBottom };
  };

  setIsWasRequestToTheSettings = (bool: boolean): void => {
    this.isWasRequestToTheSettings = bool;
  };

  /**
   * Fetches banners based on the provided CID and locale, and updates the store.
   * @param cid - The unique identifier for the client.
   * @param locale - The language locale for the banners.
   */
  getBanners = async (cid: string, locale: string) => {
    try {
      const {
        data: { banners },
      } = await fetchBanners({ cid, locale });
      runInAction(() => {
        this.banners = banners.reduce((acc, banner) => {
          banner.images.forEach((image) => (image.type = 'banner'));
          acc[banner.section] = banner;
          return acc;
        }, {});
      });
    } catch (error) {
      logger.error('Error fetching banners', {
        error,
      });
    }
  };

  setIsViewTypeWasSettledByQueryParam = (bool: boolean) => {
    this.isViewTypeWasSettledByQueryParam = bool;
  };

  /**
   * Sets the view type for the application display.
   *
   * By default, the application's display is set to 'european' view.
   * - **European**: Markets in the grid event have a horizontal layout.
   *
   * When setting the type to **"american"**:
   * - The odds format switches to "american".
   * - The locale changes to "en-us".
   * - Markets in the grid event adopt a vertical layout.
   *
   * When setting the type to **"social"**:
   * - The same changes as in "american" apply, except:
   *   - The locale changes to "en-soc".
   *   - The user is restricted from selecting other languages and odds format.
   *
   * @param {Object} config - Configuration object for view settings.
   * @param {'european' | 'american' | 'social'} config.type - The view type to set.
   * @param {string} config.locale - The locale to apply.
   * @param {string} config.oddsFormat - The odds format to apply.
   */
  setViewType = ({
    type,
    locale,
    oddsFormat,
  }: {
    type: 'european' | 'american' | 'social';
    locale?: string;
    oddsFormat?: string;
  }): void => {
    this.isAmericanView = type !== 'european';

    const lang = this.isAmericanView
      ? viewTypesSettings[type]?.locale
      : locale || LS.get('locale') || viewTypesSettings[type]?.locale || defaultLanguage;

    this.setLang({ isNoSetToLocalStorage: this.isAmericanView, locale: lang });
    this.setOddsFormat({
      isNoSetToLocalStorage: true,
      oddsFormat,
    });
    this.viewType = type;
  };

  setIsThemeSettledByThemeEditor = (bool = false) => {
    this.isThemeSettledByThemeEditor = bool;
  };

  /**
   * Sets the theme editor mode.
   * @param isEnableThemeEditor - Indicates if theme editor mode should be enabled.
   * @param isSendMessage - Specifies if a post message should be sent when changing mode.
   */
  setIsThemeEditorMode = ({
    isEnableThemeEditor,
    isSendMessage = true,
  }: {
    isEnableThemeEditor: boolean;
    isSendMessage?: boolean;
  }) => {
    this.isThemeEditorMode = isEnableThemeEditor;

    if (isSendMessage) {
      sendMsg('themeEditor', {
        isEnable: isEnableThemeEditor,
      });
    }
  };

  setIsDemoFreebetMode = (isDemoFreebetMode: boolean) => {
    this.isDemoFreebetMode = isDemoFreebetMode;
  };

  setThemeEditorData = (data?: string | Record<string, unknown>) => {
    if (!data) {
      return;
    }

    if (typeof data === 'object') {
      this.themeEditorData = data;
      return;
    }

    try {
      this.themeEditorData = data.includes('=') ? searchParamsToObject(data) : JSON.parse(data);
    } catch (error) {
      console.error(
        'Failed to parse themeEditorData: invalid format provided. themeEditorData parsing error:',
        {
          errorMessage: error?.message || '',
          originalData: data,
        },
      );
    }
  };

  setIsLightTheme = (bool = false) => {
    this.isLightTheme = bool;
  };

  updateLocationHistory = (locationPathname = '') => {
    const { list } = this.locationHistory;
    // Normalize the trailing slash: NavLink always appends "/", so the same
    // screen can arrive both as "/x" and "/x/". Without this they look like two
    // distinct entries and the "back" button (list[length - 2]) lands on the
    // current page instead of the previous one.
    const normalized = locationPathname.replace(/\/+$/, '') || '/';
    const size = list.length;
    // Skip consecutive duplicates, otherwise "back" can resolve to the current location.
    if (list[size - 1] === normalized) return;
    this.locationHistory.prevLocation = list[size - 1] || null;
    list.push(normalized);
    this.locationHistory.currentLocation = normalized;
  };

  // Target for the "back" button: the most recent history entry that is NOT the
  // current page. Walking back (instead of blindly taking list[length - 2])
  // skips trailing-slash / redirect duplicates of the current screen, so "back"
  // never points to the page we are already on.
  get backLocation() {
    const normalize = (path = '') => path.replace(/\/+$/, '') || '/';
    const { list } = this.locationHistory;
    const current = normalize(list[list.length - 1] ?? '');

    for (let i = list.length - 2; i >= 0; i -= 1) {
      const candidate = normalize(list[i] ?? '');
      if (candidate !== current) return candidate;
    }

    return '/';
  }

  setIsShowSessionExpiredPopup = (bool = false) => {
    this.isShowSessionExpiredPopup = bool;
  };

  setSocketConfigIsLoaded = (bool = true) => {
    this.socketConfigIsLoaded = bool;
  };

  setEventsToRenderOnPage = ({ isIncrement, isReset }) => {
    if (isIncrement) {
      this.eventsToRenderOnPage += this.eventsToRenderOnPageByDefault;
    } else if (isReset) {
      this.eventsToRenderOnPage = this.eventsToRenderOnPageByDefault;
    }
    setTimeout(forceCheck);
  };

  setMainMarketsFilters = (key, value) => {
    this.mainMarketsFilters[key] = value;
    LS.set('MMFilters', JSON.stringify(this.mainMarketsFilters));
  };

  setHasTaxes = (bool = true) => {
    this.hasTaxes = bool;
  };

  setLobbyNavMenuIsOpen = (bool = true) => {
    this.lobbyNavMenuIsOpen = !!bool;
  };

  setIsMatchTrackerScriptLoaded = (bool: boolean, socketId: string) => {
    this.isMatchTrackerScriptLoaded = bool;
    this.matchTrackerSocketId = socketId;
  };

  setIsMatchTrackerEnabled = (bool: boolean) => {
    this.isMatchTrackerEnabled = bool;
  };

  setOrientationByLanguage = async (lang) => {
    const attributesElement = isShadowMode ? this.appRoot.querySelector('#app-container') : html;

    if (!rtlLanguages.includes(lang)) {
      attributesElement.removeAttribute('dir');
      attributesElement.classList.remove('_rtl');
      this.isRtlOrientation = false;
      return;
    }

    this.isRtlOrientation = true;
    attributesElement.setAttribute('dir', 'rtl');
    attributesElement.classList.add('_rtl');
  };

  setTheme = async (theme: UTheme, isCustom = false) => {
    if ((this.customTheme && !isCustom) || this.isThemeSettledByThemeEditor) {
      return;
    }

    const signedTheme = themeIsValid(theme) ? theme : defaultTheme;
    this.isLoadingTheme = true;
    const themeSettings = compileTheme(signedTheme);
    if (isCustom) {
      this.customTheme = true;
    }

    const varsResponds = [];
    const themeEditorData = this.themeEditorData.vars || {};
    const themeEditorVars = Object.keys(themeEditorData).reduce((acc, currentVar) => {
      if (Array.isArray(themeEditorData[currentVar])) {
        themeEditorData[currentVar].forEach((data) => {
          const [key] = Object.keys(data);
          acc[key] = data[key];
        });
      } else {
        if (!(currentVar === 'font-url' && !themeEditorData[currentVar])) {
          acc[currentVar] = themeEditorData[currentVar];
        }
      }
      return acc;
    }, {});

    for (const { name, inherit, settings = {} } of themeSettings) {
      const inheritThemes = Array.isArray(inherit) ? inherit : [inherit];

      Object.assign(this, settings);

      const varsPath = this.root.store.assets[`dist/theme/${name}/vars.json`];

      if (varsPath) {
        const vars = await axios.get(varsPath);
        varsResponds.push(vars);
      }
    }
    let vars = varsResponds.reduce((acc, { data }) => ({ ...acc, ...data }), {});

    vars = { ...vars, ...themeEditorVars };

    const root: HTMLElement = isShadowMode
      ? (this.appRoot.host as HTMLElement)
      : document.documentElement;
    const appContainer =
      this.appRoot.getElementById('app-container') || this.appRoot.getElementById('app');

    for (const name in vars) {
      if (name === 'font-url') {
        const fontUrl: string = vars[name];
        importGoogleFont({ googleFontUrl: fontUrl, isInstallFont: true }).catch((error) => {
          logger.error('Error loading google font:', error);
        });
      } else {
        root.style.setProperty(`--${name}`, vars[name]);
      }
    }

    this.appRoot.querySelectorAll('.themecss').forEach((tag) => tag.remove());

    let headEl = document.querySelector('head');

    if (isShadowMode) {
      headEl = app.shadowRoot;
      appContainer.classList.add('_shadow-root-mode');
    }

    const customStylesEl: HTMLLinkElement | null = this.appRoot.querySelector('#customStyles');
    const iterations = themeSettings.length;

    for (const { name } of themeSettings) {
      appContainer.classList.add(name);
      const themeId = `${name}_theme_id`;

      const prevThemeStyles: HTMLStyleElement | HTMLLinkElement | null = headEl.querySelector(
        `#${themeId}`,
      );
      if (prevThemeStyles) {
        if (customStylesEl) {
          stylesInjector(prevThemeStyles, null, customStylesEl);
        } else {
          stylesInjector(prevThemeStyles, headEl);
        }
      } else {
        const filePath = this.root.store.assets[`${name}_theme.css`];

        if (filePath) {
          const cssLink: HTMLLinkElement = createEl({
            attr: {
              href: filePath,
              rel: 'stylesheet',
              type: 'text/css',
            },
            className: 'themecss',
            id: themeId,
            tag: 'link',
          });

          if (customStylesEl) {
            stylesInjector(cssLink, null, customStylesEl);
          } else {
            stylesInjector(cssLink, headEl);
          }

          try {
            cssLink.onerror = () => {
              this.isLoadingTheme = false;
            };
            if (!iterations - 1) {
              cssLink.onload = () => {
                this.isLoadingTheme = false;
              };
            }
          } catch (error) {
            if (error instanceof Error) {
              logger.error('Error adding listeners on cssLink', {
                error,
              });
              this.isLoadingTheme = false;
            }
          }
        }
      }
    }
    this.isThemeLoaded = true;
    this.isLoadingTheme = false;
    this.theme = signedTheme;
    LS.set('color_theme', signedTheme);
  };

  setLineFilter = (filter) => {
    this.lineFilter = filter;
  };

  setTopNavOrientation = (bool = true) => {
    this.isTopNavOrientation = bool;
  };

  setAnimalsRacingView = (bool = true) => {
    this.setTopNavOrientation(bool);
    this.isAnimalsRacingView = bool;
  };

  setMainMenuExpand = (id: string) => {
    if (id) {
      this.mainMenuExpands = [id];
    } else {
      this.mainMenuExpands = [];
    }
  };

  setMobileSliderInteraction = (id: string, action: 'remove' | 'add') => {
    if (action === 'remove') {
      this.mobileSliderInteraction = this.mobileSliderInteraction.filter((i) => i !== id);
    }
    if (action === 'add') {
      this.mobileSliderInteraction = [...this.mobileSliderInteraction, id];
    }
  };

  setOddsFormat = ({ oddsFormat, isNoSetToLocalStorage = false }) => {
    if (oddsFormats[oddsFormat]) {
      this.oddsFormat = oddsFormat;

      if (isNoSetToLocalStorage) {
        return;
      }
      LS.set('oddsFormat', oddsFormat);
    }
  };

  setPageWithChat = (withChat = false) => {
    this.pageWithChat = withChat;
  };

  setIsLargeScreen = () => {
    this.isLargeScreen = isLargeScreen();
  };

  setIsMobile = () => {
    this.isMobile = isMobile();
  };

  setIsTablet = () => {
    this.isTablet = isTablet();
  };

  setModalMybetsOpen = (open = true) => {
    this.modalMybetsOpen = open;
  };

  setModalSettingsOpen = (open = true) => {
    this.modalSettingsOpen = open;
  };

  setBetsHistoryOpen = (open = true) => {
    this.betHistoryOpen = open;
  };

  setModalConditionalCashoutOpen = (open = true) => {
    this.modalConditionalCashoutOpen = open;
  };

  setCurrentConditionalCashoutBetData = (data: Record<string, unknown> | null = null) => {
    this.currentConditionalBetData = data;
  };

  prevActiveTab = 'betslip';
  setLiveView = (liveView = false) => {
    this.liveView = liveView;
    if (liveView) {
      this.prevActiveTab = this.activeTab;
      this.setActiveTab('live-bet');
    } else {
      this.setActiveTab(this.prevActiveTab);
    }
  };

  gridEventItemPreviewId = ({ disciplineId, previewsNum = 10, removeId = '', reset, isSimple }) => {
    if (reset) {
      this.gridEventItemsPreviews = new Map();
      return;
    }
    let currentView = this.gridEventItemsPreviews.get(disciplineId);

    if (removeId && currentView) {
      currentView.delete(removeId);
      if (!currentView.size) {
        this.gridEventItemsPreviews.delete(disciplineId);
      }
      return;
    }

    if (isSimple) {
      if (!currentView || currentView?.size === previewsNum) {
        currentView = new Set();
        this.gridEventItemsPreviews.set(disciplineId, currentView);
      }

      for (let i = 1; i <= previewsNum; i++) {
        if (!currentView.has(i)) {
          currentView.add(i);
          return i;
        }
      }
    }

    const randomNums = getRandomNums(previewsNum, previewsNum);
    const previewId = randomNums[0];

    if (!currentView || currentView?.size === previewsNum) {
      this.gridEventItemsPreviews.set(disciplineId, new Set([previewId]));
      return previewId;
    }

    for (let i = 0; i < previewsNum; i++) {
      const randomNum = randomNums[i];
      const hasTargetId = currentView.has(randomNum);
      if (!hasTargetId) {
        currentView.add(randomNum);
        return randomNum;
      }
    }
  };

  get isAppFullWidth() {
    const allowedGap = 25;
    const appGap = window.innerWidth - this.appWidth;

    return appGap < allowedGap;
  }

  get isNarrowAppSize() {
    return this.appWidth >= 770 && this.appWidth <= 980;
  }

  get isCompactMode() {
    return !this.isAppFullWidth && this.isNarrowAppSize;
  }

  get allDisciplinesAreShown() {
    const {
      lobbyFilters: { disciplines },
    } = this;
    return disciplines.length === this.disciplineIds.length;
  }

  // Check theme and device type for showing navigation or not
  get isShowEventNav() {
    return !(this.isMobile || this.isTablet) || this.isTopNavOrientation;
  }

  get lineFilterDisciplineCategory() {
    if (!this.lineFilter) {
      return undefined;
    }

    if (Array.isArray(this.lineFilter)) {
      return this.lineFilter.reduce((acc, item) => {
        const category = getDisciplineCategoriesFromLineFilter(item);

        if (category) {
          acc.add(category);
        }

        return acc;
      }, new Set());
    }

    const formattedDisciplineCategory = lineFilterToDisciplineCategories[this.lineFilter];

    if (formattedDisciplineCategory) {
      return [formattedDisciplineCategory];
    }

    return [getDisciplineCategoriesFromLineFilter(this.lineFilter)];
  }

  setActiveTab = (tab = 'betslip') => {
    this.activeTab = tab;
  };

  setDisciplinesByType = (lobbyDisciplines) => {
    const sportIds = disciplineIds.filter((id) => !esportDisciplines.includes(id));
    const esportIds = esportDisciplines.filter((id) => !id.includes('-bots'));
    const vsportIds = esportDisciplines.filter((id) => id.includes('-bots'));

    const getDisciplineType = (disciplineId) => {
      switch (disciplineId) {
        case sportIds.filter((id) => disciplineId === id)[0]:
          return 'sport';
        case esportIds.filter((id) => disciplineId === id)[0]:
          return 'esport';
        case vsportIds.filter((id) => disciplineId === id)[0]:
          return 'vsport';
        default:
          return '';
      }
    };

    const disciplinesByType: DisciplinesByTypeObject = {
      esport: [],
      sport: [],
      vsport: [],
    };

    lobbyDisciplines.forEach((el) => {
      const { id, children = [] } = el;
      const type = getDisciplineType(id);
      if (!type) {
        console.error(
          `Discipline "${id}" not found in constants. Please add it to this file "@/constants/index.js"`,
        );
        return;
      }
      disciplinesByType[type].push({ children, id, type });
    });

    const setLinkedLDisciplines = (currentType, childType, parentType) => {
      disciplinesByType[currentType].forEach((parent) => {
        const isWithChildDiscipline = !parent.children.length;
        if (parentType) {
          disciplinesByType[parentType].forEach((el) => {
            if (parent.id.includes(el.id)) {
              parent.parentDiscipline = el.id;
            }
          });
        }
        if (childType && isWithChildDiscipline) {
          disciplinesByType[childType].forEach((el) => {
            if (el.id.includes(parent.id)) {
              parent.childDiscipline = el.id;
            }
          });
        }
      });
    };
    const disciplinesRelations = {
      esport: ['vsport', 'sport'],
      sport: ['esport', null],
      vsport: [null, 'esport'],
    };
    for (const key in disciplinesByType) {
      setLinkedLDisciplines(key, ...disciplinesRelations[key]);
    }

    this.disciplinesWithTypes = Object.values(disciplinesByType).reduce(
      (acc, val) => [...acc, ...val],
      [],
    );
  };

  setCoreSettings = (settings) => {
    const {
      setIsShowComboBooster,
      setComboBoosterSettings,
      setBetItemWithMaxBet,
      setIsExpressPresetBetSlip,
    } = this.root.betslip;

    this.root.search.setSearchTriggerLocation(settings.theme.searchPosition ?? 'middle');

    const {
      theme,
      menu: { games = [] },
      comboboostSettings = {},
      formatOdds,
      isQuickBet,
      themeSettings,
      newFunctionalMaxBet,
      viewType,
      expressPresetBetSlip = null,
      racingFormat,
      sportsNavigation = 'basic',
      sameEventParlays = false,
      personalizedEvents,
      presetLastBetSum,
      showBalance,
      betBuilder = 0,
      comboboost = 0,
      swipeBet,
    } = settings;

    if (!this.isViewTypeWasSettledByQueryParam) {
      this.setViewType({
        oddsFormat: LS.get('oddsFormat') || formatOdds || defaultOddsFormat,
        type: viewType,
      });
    }

    setIsShowComboBooster(Boolean(comboboost));
    setComboBoosterSettings(comboboostSettings);
    setBetItemWithMaxBet(!!newFunctionalMaxBet);
    setIsExpressPresetBetSlip(!!expressPresetBetSlip);
    this.setIsPersonalizedEvents(!!personalizedEvents);
    this.root.categorizer.setSportsNavigation(sportsNavigation);
    this.root.betBuilder.setIsBetBuilderActive(Boolean(betBuilder));
    this.setPresetLastBetSum(presetLastBetSum);
    this.setShowSwipeBet(swipeBet);
    this.setIsShowBalance(showBalance);
    this.root.expressPresets.setIsBetBuilder(sameEventParlays);

    try {
      if (themeSettings) {
        this.setThemeEditorData(JSON.parse(themeSettings));
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Theme settings error', {
          error,
        });
      }
    }

    this.isShowQuickBets = !!isQuickBet;

    if (!(this.customTheme || this.isSettledCoreTheme)) {
      this.setTheme(theme?.type);
      this.isSettledCoreTheme = true;
    }

    if (theme.widgets) {
      this.allowedWidgets = theme.widgets;
    }

    if (racingFormat === 1) {
      this.setAnimalsRacingView();
    }

    this.coreSettingsIsLoaded = true;
  };

  setSettings = (disciplines) => {
    runInAction(() => {
      const { lineFilter } = this;
      let filteredDisciplines = disciplines;

      if (lineFilter) {
        switch (true) {
          case lineFilter === 'esport':
            filteredDisciplines = disciplines.filter(({ id }) => esportDisciplines.includes(id));
            break;
          case lineFilter === 'sport':
            {
              const sportDisciplines = disciplineIds.filter(
                (id) => !esportDisciplines.includes(id),
              );
              filteredDisciplines = disciplines.filter(({ id }) => sportDisciplines.includes(id));
            }
            break;
          case Array.isArray(lineFilter):
            filteredDisciplines = disciplines.filter(({ id }) => lineFilter.includes(id));
            break;
          default:
            break;
        }
      }

      const mmaFix = (i) => i;
      this.disciplineIds = filteredDisciplines.reduce((acc, { id, children }) => {
        if (!disciplineIds.includes(id)) {
          return acc;
        }
        if (children.length) {
          const ids = children.map((i) => mmaFix(i));
          return [...acc, mmaFix(id), ...ids];
        }
        return [...acc, mmaFix(id)];
      }, []);

      this.disciplineIdsAliases = filteredDisciplines.reduce((acc, { id, children }) => {
        const discipline = mmaFix(id);
        if (children.length) {
          const mainId = mmaFix(id);
          return {
            ...acc,
            ...children.reduce(
              (a, i) => ({
                ...a,
                [mmaFix(i)]: mainId,
              }),
              { [mainId]: mainId },
            ),
          };
        }
        if (acc[discipline]) return acc;

        return { ...acc, [discipline]: discipline };
      }, {});

      this.disciplineIdsSubscriptionAliases = filteredDisciplines.reduce(
        (acc, { id, children }) => {
          if (children.length) {
            return {
              ...acc,
              [id]: [id, ...children.map((id) => id)],
            };
          }
          if (acc[id]) return acc;
          return { ...acc, [id]: [id] };
        },
        {},
      );
      this.socketConfigIsLoaded = true;
      this.setDisciplinesByType(filteredDisciplines);
    });
  };

  setLocaleLang = ({ locale = '', isNoSetToLocalStorage = false }) => {
    const language = locale || defaultLanguage || '';
    this.language = language;

    if (isNoSetToLocalStorage) {
      return;
    }
    LS.set('locale', language);
  };

  setLang = ({ locale = '', isNoSetToLocalStorage = false }) => {
    const {
      language,
      root: { betslip },
      setLocaleLang,
    } = this;

    const langToSet = getLocaleLang(locale);

    this.setOrientationByLanguage(langToSet);

    if (langToSet === language) {
      setLocaleLang({ isNoSetToLocalStorage, locale: langToSet });
      return;
    }

    i18next.loadLanguages([langToSet]).then(() => {
      i18next.changeLanguage(langToSet);
      set(this, 'language', langToSet);
      setLocaleLang({ isNoSetToLocalStorage, locale: langToSet });

      if (Socket.closeSocketAndReInit) {
        Socket.setConfig({
          language: langToSet,
        });
        Socket.closeSocketAndReInit({ isClearStore: true });
      }
      betslip.updateBets();
    });
  };

  setDisciplineView = (disciplineId, type) => {
    const savedViews = JSON.parse(LS.get('disciplineViews')) || {};
    this.disciplinesViews[disciplineId] = type;
    savedViews[disciplineId] = type;
    LS.set('disciplineViews', JSON.stringify(savedViews));
  };

  setIsBonusesModalOpen = (isOpen = false) => {
    this.isBonusesModalOpen = isOpen;
  };

  setBetSlipOpen = (isOpen) => {
    const wasOpen = this.betSlipOpen;
    this.betSlipOpen = isOpen;
    const { resetData, setNotice, inProgress } = this.root.betslip;
    if (!inProgress) {
      resetData();
    }

    if (!isOpen) {
      if (html) {
        html.classList.remove('betslip-open');
      }
      const { timeouts } = window;
      if (timeouts) {
        Object.keys(timeouts).forEach((key) => {
          clearTimeout(timeouts[key]);
          delete timeouts[key];
          setNotice(key, false);
        });
      }
      this.root.expressPresets.setIsShowTurboComboMobile(false);
    } else if (html) {
      html.classList.add('betslip-open');

      if (this.isMobile && !wasOpen) {
        setLazyLoadScrollSuppressed(true);
        sendMsg('click', { target: 'scrollToTop' });
        setTimeout(() => {
          setLazyLoadScrollSuppressed(false);
          forceCheck();
        }, 600);
      }
    }
  };

  setLobbyFilter = (type: string, names: string[] | string = []) => {
    if (Array.isArray(names) && isEqualArrays(this.lobbyFilters[type], names)) {
      return;
    }
    if (type === 'disciplines' && !names.length) {
      this.lobbyFilters.disciplines = this.disciplineIds;
    } else {
      this.lobbyFilters[type] = names;
    }

    if (type !== 'date' && type !== 'tournament') {
      this.lobbyFilters.date = null;
    }
  };

  resetDisciplines = () => {
    const {
      setLobbyFilter,
      allDisciplinesAreShown,
      disciplinesIsSport,
      root: {
        history: {
          push,
          location: { pathname },
        },
        store: { lobbyDisciplinesList },
      },
    } = this;

    const isTournament = pathname.includes('/tournaments');
    const isLobby = pathname === '/';
    if (isLobby) {
      const disciplines = lobbyDisciplinesList.map(({ id }) => id);
      setLobbyFilter('disciplines', disciplines);
      return;
    }

    if (!allDisciplinesAreShown) {
      switch (true) {
        case disciplinesIsSport && isTournament:
          push('/tournaments');
          break;
        case !disciplinesIsSport && isTournament:
          push('/tournaments');
          break;
        case disciplinesIsSport:
          push('/sports');
          break;
        case !(disciplinesIsSport || isLobby):
          push('/');
          break;
        default:
          push('/');
          break;
      }
    }
  };

  setLobbyDiscipline = (id) => {
    const {
      setLobbyFilter,
      lobbyFilters: { disciplines },
      root: {
        store: { lobbyDisciplinesList },
      },
    } = this;
    if (lobbyDisciplinesList.every((el) => disciplines.includes(el.id))) {
      return setLobbyFilter('disciplines', [id]);
    }
    if (disciplines.length === 1 && disciplines.includes(id)) {
      return this.resetDisciplines();
    }
    if (disciplines.includes(id)) {
      const arr = disciplines.filter((item) => item !== id);
      return setLobbyFilter('disciplines', [...arr]);
    }
    if (!disciplines.includes(id)) {
      return setLobbyFilter('disciplines', [...disciplines, id]);
    }
  };

  resetScheduleDisciplines = (interval: string) => {
    const { setLobbyFilter, allDisciplinesAreShown } = this;
    const { push } = this.root.history;
    if (!allDisciplinesAreShown) {
      setLobbyFilter('disciplines', this.disciplineIds);
      push(`/results/${interval}`);
    }
  };

  resetLobbyFilters = (
    filtersToReset = {
      country: null,
      date: null,
      disciplines: [],
      lines: [],
      tournament: null,
    },
  ) => {
    this.lobbyFilters = {
      ...this.lobbyFilters,
      ...filtersToReset,
    };
  };

  setBlockVideo = (block = true) => {
    this.blockVideo = block;
  };

  runVideo = (status = true) => {
    this.playVideo = status;

    if (this.root.auth.demo) {
      if (status) {
        clearTimeout(this.timeoutBroadcast);
        this.timeoutBroadcast = setTimeout(() => {
          this.setBlockVideo();
          this.setAlert({
            title: 'LOBBY.ERROR.MSG.LOGIN',
            type: 'warning',
          });
        }, 60000 * 2);
      } else {
        clearTimeout(this.timeoutBroadcast);
      }
    }
  };

  setDisciplinesType = (type) => {
    if (type === this.disciplinesIsSport) return;
    if (
      (this.disciplinesIsSport ? type !== 'sport' : type !== 'esport') ||
      this.disciplinesIsSport === 'lobby'
    ) {
      if (type === 'lobby') {
        this.disciplinesIsSport = type;
        LS.set('disciplinesIsSport', type);
        return;
      }
      this.disciplinesIsSport = type === 'sport';
      LS.set('disciplinesIsSport', this.disciplinesIsSport);
    }
  };

  /**
   * Sets an alert with specified properties or updates an existing alert if it matches the criteria.
   * @param {Object} alert - The alert object containing properties such as title, type, text, id, and timeout.
   * @param {string} [alert.title] - The title of the alert.
   * @param {string} [alert.type] - The type of the alert (e.g., 'success', 'warning', 'error').
   * @param {string} [alert.text] - The text content of the alert.
   * @param {string} [alert.id] - The unique identifier of the alert.
   * @param {boolean} [alert.progress] - If set to true - will draw a progress bar for hiding the alert.
   * @param {boolean} [alert.hideBtnClose] - Hides the close button of the alert,
   * but only if alert.progress or alert.button or alert.undo is settled.
   * @param {object} [alert.button] - If specified, a button with the assigned text is drawn.
   * A button can contain a route path or a function to execute when the button is clicked.
   * @param {number|boolean} [alert.timeout] - The timeout duration for the alert to automatically hide.
   * @param {function} [alert.callback] - A function that will be executed after the alert is closed.
   * Or prevents the alert from being hidden if set to - false.
   * @param {boolean} [isPreventSameAlertReRender] - Whether to prevent re-rendering the same alert.
   * If true - does not make changes to the timeout of an already existing identical alert.
   * @returns {Alert} - Returns the newly created or updated Alert object.
   */
  setAlert = (alert, isPreventSameAlertReRender = false) => {
    const { title = '', type = '', text = '', id = '', timeout } = alert;

    const alertId = `${title}-${type}-${text}-${id}`;
    const sameAlert = this.alerts.find((el) => {
      const currentAlertId = `${el.title}-${el.type}-${el.text}-${el.id}`;
      return currentAlertId === alertId;
    });
    if (!sameAlert) {
      const newAlert = new Alert(alert);
      this.alerts.push(newAlert);
      return newAlert;
    }
    if (!isPreventSameAlertReRender) {
      const currentTimeout =
        typeof timeout !== 'undefined' ? timeout : sameAlert.alertDefaultTimeout;
      clearTimeout(sameAlert.timeoutId);

      if (!Number.isNaN(currentTimeout) && currentTimeout !== false) {
        sameAlert.timeoutId = setTimeout(() => {
          sameAlert.hide();
        }, currentTimeout);
      } else {
        sameAlert.timeoutId = null;
      }
    }

    return sameAlert;
  };

  getAlert = (alert) => {
    const { title = '', type = '', text = '', id = '' } = alert;
    const alertId = `${title}-${type}-${text}-${id}`;
    return this.alerts.find((el) => {
      const currentAlertId = `${el.title}-${el.type}-${el.text}-${el.id}`;
      return currentAlertId === alertId;
    });
  };

  checkAlerts = () => {
    this.alerts = this.alerts.filter(({ shown }) => !shown);
  };

  hideAlerts = () => {
    this.alerts.forEach((alert) => {
      alert.hide();
    });
    this.alerts = [];
  };

  applyCustomStyles = async (url: string) => {
    try {
      const id = 'customStyles';
      if (!this.appRoot.getElementById(id)) {
        const target: ShadowRoot | HTMLElement | Document = isShadowMode
          ? this.appRoot
          : document.head;
        const link = document.createElement('link');

        link.id = id;
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = decodeURIComponent(url);
        link.media = 'all';
        stylesInjector(link, target);
      }
    } catch (error) {
      logger.error('Error loading partner custom styles', {
        error,
      });
    }
  };

  setIsSLG = (bool = false) => {
    this.isHideVideo = bool;
    this.isSLG = bool;
  };

  setRoutSlug = (slug: string) => {
    this.routSlug = slug;
  };

  setAppWidth = (width: number) => {
    this.appWidth = width;
  };

  setAppHeight = (height: number) => {
    this.appHeight = height;
  };

  setIsRocketAnimation = (bool = false) => {
    this.isRocketAnimation = bool;
  };

  setIsSDK = (bool = false) => {
    this.isSDK = bool;
  };

  setHasParentIframe = (bool = false) => {
    this.hasParentIframe = bool;
  };

  /**
   *  Set the: mainFrameUrl, loginUrl, depositUrl, registerUrl
   * @param urls
   */
  setSdkUrls = (
    urls: {
      mainFrameUrl?: string;
      loginUrl?: string;
      depositUrl?: string;
      registerUrl?: string;
    } = {},
  ): void => {
    const allowedUrls = ['mainFrameUrl', 'loginUrl', 'depositUrl', 'registerUrl'];
    Object.entries(urls).forEach(([urlName, urlValue]) => {
      if (urlValue) {
        if (urlValue === 'sendPostMessage') {
          if (urlName === 'mainFrameUrl') {
            logger.warn('Cannot set mainFrameUrl to "sendPostMessage"');
          } else {
            this[urlName] = urlValue;
          }
          return;
        }
        if (allowedUrls.includes(urlName)) {
          this[urlName] = /^https?:\/\//.test(urlValue) ? urlValue : `https://${urlValue}`;
        } else {
          logger.warn(`Url ${urlName} is not allowed to set by "setSdkUrls" method`);
        }
      }
    });
  };

  setIsHideExpressPresets = (bool = false) => {
    this.isHideExpressPresets = bool;
  };

  // Set boolean true if route is Event Page
  setIsEventPage = (bool = false) => {
    this.isEventPage = bool;
  };

  getDemoProfileBonuses = async () => {
    if (isDemoLocation && this.isDemoFreebetMode) {
      try {
        const currentBrand =
          this.root.auth.brand === 'beterdemo' ? 'turbodemo' : this.root.auth.brand;

        const response = await createBonusProfile(currentBrand, this.root.auth.token);
        if (response.status === 200) {
          const { getBonuses } = this.root.betslip;
          await getBonuses();
        }
      } catch (error) {
        logger.error('Request for createBonusProfile failed', { error: error as Error });
      }
    }
  };

  setIsPersonalizedEvents = (bool = false) => {
    this.isPersonalizedEvents = bool;
  };

  setAppRoot = (el: ShadowRoot | Document) => {
    this.appRoot = el;
  };

  /**
   * Sets the main scrollable element for the application.
   * This method updates the `scrollableElement` with the provided element.
   *
   * @param element - The element to set as the scrollable element.
   *                  Can be either an `HTMLElement` or `null`.
   */
  setScrollableElement = (element: HTMLElement | null) => {
    this.scrollableElement = element;
  };

  setCustomizedTournaments = (
    tournaments: { tournamentId: string; disciplineId: string }[] | string[],
  ) => {
    this.customizedTournaments = tournaments;
  };

  setIsCustomizedTournamentsPage = (bool: boolean) => {
    this.isCustomizedTournamentsPage = bool;
  };

  setIsCustomizedHomePage = (bool: boolean) => {
    this.isCustomizedHomePage = bool;
  };

  setBannerBetslip = (banner: any | string | undefined) => {
    if (!banner || typeof banner === 'string') {
      this.bannerBetslip = null;
    } else {
      this.bannerBetslip = banner;
    }
  };

  setBannerBetslipUrl = (url: string = '') => {
    if (this.bannerBetslip) {
      this.bannerBetslip = { ...this.bannerBetslip, url };
    }
  };

  setPresetLastBetSum = (bool: boolean) => {
    this.presetLastBetSum = bool;
  };

  setShowSwipeBet = (bool: boolean) => {
    this.showSwipeBet = bool;
  };

  setIsShowBalance = (bool: boolean) => {
    this.showBalance = bool;
  };
}
